'use client';

import { useReveal } from '@/hooks/use-reveal';
import { Hammer, Scissors, Sparkles, Award } from 'lucide-react';

const STEPS = [
  {
    icon: Scissors,
    title: 'Sélection du Cuir',
    text: "Chaque peau est examinée à la main, à la lumière du jour. Nous ne conservons que les cuirs pleine fleur, sans défaut, au grain serré et à la main franche.",
  },
  {
    icon: Hammer,
    title: "Coupe & Montage",
    text: "Le patron est tracé à la main sur le cuir, puis découpé au scalpel. Les pièces sont assemblées au point sellier, point par point, sans aucune précipitation.",
  },
  {
    icon: Sparkles,
    title: 'Patine & Finition',
    text: "Le cuir est teint à la main, ciré à la cire d'abeille, puis lissé à la pierre d'agate. Cette finition révèle les nuances et protège la pièce pour des décennies.",
  },
  {
    icon: Award,
    title: 'Contrôle & Signature',
    text: "Chaque pièce est inspectée, numérotée et signée par l'artisan qui l'a façonnée. Un certificat d'authenticité accompagne chaque création.",
  },
];

export default function Atelier() {
  const { ref, visible } = useReveal();

  return (
    <section id="atelier" ref={ref} className="relative py-28 md:py-40 bg-[#1A0E08] overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/atelier-workshop.png"
          alt="Atelier ASMAO STYLE CUIR Paris"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A0E08] via-[#1A0E08]/85 to-[#1A0E08]" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] font-serif-display text-sm tracking-[0.4em] uppercase">
                L'Atelier
              </span>
              <div className="w-10 h-px bg-[#C9A961]" />
            </div>
          </div>

          <h2 className={`reveal ${visible ? 'visible' : ''} font-display text-[#F8F1E4] text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8`}>
            Là où le Cuir
            <br />
            <span className="italic text-luxe-gold">prend Âme</span>
          </h2>

          <p className={`reveal ${visible ? 'visible' : ''} text-[#F8F1E4]/80 text-lg md:text-xl font-serif-display font-light leading-relaxed`}>
            Notre atelier parisien est un lieu hors du temps. Ici, les machines cèdent la place
            aux mains, les cadences à la patience, la production à la création. Chaque pièce
            nécessite entre 12 et 60 heures de travail, selon sa complexité.
          </p>
        </div>

        {/* Process steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <div
                key={step.title}
                className={`reveal ${visible ? 'visible' : ''} group relative`}
                style={{ transitionDelay: `${idx * 120}ms` }}
              >
                {/* Step number */}
                <div className="absolute -top-2 -left-2 font-display text-6xl text-[#C9A961]/15 group-hover:text-[#C9A961]/40 transition-colors duration-500 select-none">
                  {String(idx + 1).padStart(2, '0')}
                </div>

                <div className="relative pt-8 pb-6 px-6 border-t border-[#C9A961]/30 group-hover:border-[#C9A961] transition-colors duration-500">
                  <Icon
                    className="text-[#C9A961] mb-5"
                    size={36}
                    strokeWidth={1.25}
                  />
                  <h3 className="font-display text-xl text-[#F8F1E4] mb-3 tracking-wide">
                    {step.title}
                  </h3>
                  <p className="text-[#F8F1E4]/65 text-sm font-serif-display font-light leading-relaxed">
                    {step.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Atelier quote */}
        <div className={`reveal ${visible ? 'visible' : ''} mt-24 max-w-4xl mx-auto text-center`}>
          <div className="divider-gold w-32 mx-auto mb-8" />
          <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-[#F8F1E4] italic font-light leading-relaxed">
            « Dans notre atelier, le temps n'est pas une contrainte.
            <br />
            <span className="text-luxe-gold">C'est un ingrédient.</span> »
          </blockquote>
          <div className="mt-8 text-[#C9A961] text-xs tracking-[0.3em] uppercase font-serif-display">
            — Maître Artisan, Atelier ASMAO
          </div>
        </div>
      </div>
    </section>
  );
}
